// TODO(SuperPaintman): Remove those rules later
/* tslint:disable:no-floating-promises no-inferred-empty-object-type no-use-before-declare max-classes-per-file newline-before-return newline-per-chained-call prefer-switch */

// Modules
import React, {Component} from 'react'
import {withRouter, RouteComponentProps, Router} from 'react-router'
import {isLocked, Lock} from '@eda.yandex/ui-kit/lib/lock'
import {Path} from 'history'
import url from 'url'
import {isEqual} from 'lodash-es'
// Aliases
import {RouteState, GLOBAL_SCROLL_ELEMENT_ATTR} from './lib/constants'
// Current directory
import {
  RouteManager, ILocation, RouteManagerEvents, ScrollBehavior,
  WithLegacyRoutesConfigProps, locationDescriptorToString,
  enhanceLocationToWithBaseURL
} from './index'
import path from 'path'

type OuterRendererProps = {
  /**
   * Роут-менеджер
   */
  routeManager: RouteManager<any>
  /**
   * Нужно ли предзагружать данные в фоновом режиме, когда перерисовали роут
   */
  backgroundPreload?: boolean
}

type BrowserRouterProps<Env> = WithLegacyRoutesConfigProps & {
  routeManager: RouteManager<Env>
}

type InnerRendererProps = OuterRendererProps & RouteComponentProps

function getGlobalScrollElement () {
  return document.querySelector(`[${GLOBAL_SCROLL_ELEMENT_ATTR}]`)
}

function getScroll () {
  const globalScrollElement = getGlobalScrollElement()

  if (globalScrollElement) {
    return globalScrollElement.scrollTop
  }
  return window.pageYOffset || document.documentElement!.scrollTop
}

function setScroll (scroll: number) {
  const globalScrollElement = getGlobalScrollElement()

  if (globalScrollElement) {
    globalScrollElement.scrollTop = scroll
  } else {
    window.scrollTo(0, scroll)
  }
}

export function isEqualUrls (leftUrl: string, rightUrl: string): boolean {
  const left = url.parse(leftUrl, true)
  const right = url.parse(rightUrl, true)

  return isEqual(left.query, right.query)
}

const historyStack: ILocation[] = []
const historyByKey: {
  [k: string]: {
    index: number
    scrollTop?: number
    location: ILocation
  }
} = {}
let historyIndexCounter = 0

const Renderer = withRouter(class extends Component<InnerRendererProps> {

  componentDidMount () {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }

  componentWillReceiveProps (nextProps: InnerRendererProps) {
    // Check if this is silent replace
    const ignoreUpdate = nextProps.history.action === 'REPLACE' &&
      Boolean(nextProps.location.state && nextProps.location.state[RouteState.SILENT_REPLACE]) ||
      nextProps.history.action === 'PUSH' &&
      Boolean(nextProps.location.state && nextProps.location.state[RouteState.SILENT_PUSH]) ||
      isLocked(Lock.BrowserHistory)
    if (nextProps.location !== this.props.location && !ignoreUpdate) {
      this.updateLocation(nextProps.location)
    }
  }

  updateLocation (newLocation: ILocation) {
    const {action} = this.props.routeManager.history
    let prevScroll: number | undefined
    let resultScroll: number | undefined

    if (action === 'POP') {
      const popLocation = historyByKey[this.props.routeManager.history.location.key]
      prevScroll = popLocation && popLocation.scrollTop
    }
    Promise.all([
      this.props.routeManager.updateLocation(newLocation).then((result) => {
        if (result.redirectTo) {
          this.props.history.replace(result.redirectTo)
          return
        }
        if (result.scroll === undefined) {
          resultScroll = prevScroll !== undefined ? prevScroll : 0
        } else if (result.scroll === ScrollBehavior.Top) {
          resultScroll = 0
        } else if (result.scroll === ScrollBehavior.Restore) {
          resultScroll = prevScroll
        }
      }),
      new Promise((resolve) => {
        this.props.routeManager.emitter.once(RouteManagerEvents.ComponentsUpdated, resolve)
      })
    ]).then(() => {
      if (resultScroll !== undefined) {
        setScroll(resultScroll)
      }
    })
  }

  render () {
    return this.props.routeManager.renderRoutes()
  }
})

export function BrowserRender (props: OuterRendererProps) {
  return (
    <BrowserRouter routeManager={props.routeManager}>
      <Renderer {...props} />
    </BrowserRouter>
  )
}

class BaseBrowserRouter<Env> extends Component<BrowserRouterProps<Env>> {
  constructor (props: BrowserRouterProps<Env>) {
    super(props)
    const {history, baseURL} = this.props.routeManager
    const originalPush = history.push.bind(history)
    const originalReplace = history.replace.bind(history)

    history.push = (to: any, ...rest: any[]) => {
      originalPush(enhanceLocationToWithBaseURL(to, baseURL), ...rest)
    }
    history.replace = (to: any, ...rest: any[]) => {
      originalReplace(enhanceLocationToWithBaseURL(to, baseURL), ...rest)
    }

    history.silentReplace = (to: Path) => {
      history.replace(to, {[RouteState.SILENT_REPLACE]: true})
    }
    history.silentPush = (to: Path) => {
      history.push(to, {[RouteState.SILENT_PUSH]: true})
    }
    history.tryGoBack = (to: Path) => {
      // Пытаемся вернуться назад через браузерный goBack, если это возможно
      // Иначе оформляем как переход через PUSH, чтобы не уходить с сайта
      const lastGoBackLocation = historyStack[historyStack.length - 2]

      if (to.indexOf(baseURL) !== 0) {
        to = path.join(baseURL, to)
      }
      if (
        lastGoBackLocation &&
        isEqual(
          locationDescriptorToString(to),
          locationDescriptorToString(lastGoBackLocation)
        )
      ) {
        history.goBack()
      } else {
        history.push(to)
      }
    }

    history.listen((location: ILocation, action) => {
      if (isLocked(Lock.BrowserHistory)) {
        return
      }
      const lastGoBackLocation = historyStack[historyStack.length - 2]
      const lastLocation = historyStack[historyStack.length - 1]
      let updateScrollLastLocation: ILocation | null = null

      if (action === 'POP') {
        if (historyByKey[location.key] && lastLocation && historyByKey[lastLocation.key] &&
          historyByKey[lastLocation.key].index < historyByKey[location.key].index) {
          // Переход вперед, через системную кнопку "Вперед"
          historyStack.push(location)
          updateScrollLastLocation = lastLocation
        } else if (
          lastGoBackLocation &&
          locationDescriptorToString(lastGoBackLocation) === locationDescriptorToString(location)
        ) {
          // Переход назад через системную кнопку "Назад"
          historyStack.pop()
          updateScrollLastLocation = lastLocation
        } else {
          // Переход "Вперед", который мы по каким то причинам не смогли отловить в первом условии
          // Например, после перезагрузки страницы
          historyByKey[location.key] = {
            location,
            index: ++historyIndexCounter
          }
          historyStack.push(location)
          updateScrollLastLocation = lastLocation
        }
      } else if (action === 'REPLACE') {
        // Замещаем последнюю запись в истории, при этом положении скролла никакое НЕ сохраняем
        historyByKey[location.key] = {
          location,
          index: historyIndexCounter
        }
        historyStack.pop()
        historyStack.push(location)
      } else if (action === 'PUSH') {
        // Переход на новую страницу через браузерный history.push, или например через ссылки от react-router
        historyByKey[location.key] = {
          location,
          index: ++historyIndexCounter
        }
        updateScrollLastLocation = lastLocation
        historyStack.push(location)
      }

      // Сохраняем позицию скролла для последнего location перех переходом
      if (updateScrollLastLocation && historyByKey[updateScrollLastLocation.key]) {
        historyByKey[updateScrollLastLocation.key].scrollTop = getScroll()
      }
    })

    // Почему то здесь иногда присутствует key, а иногда нет
    // TODO разобраться с этим
    historyByKey[history.location.key] = {
      location: history.location,
      index: historyIndexCounter++
    }
    historyStack.push(history.location)
  }

  render () {
    return <Router history={this.props.routeManager.history} children={this.props.children} />
  }
}

export const BrowserRouter = BaseBrowserRouter
